import { supabase } from '@/integrations/supabase';
import {
  UserInteraction,
  ShowcaseComment,
  mapDbToUserInteraction,
  mapUserInteractionToDb,
  mapDbToShowcaseComment,
  mapShowcaseCommentToDb,
  showcaseInteractionSchema,
  showcaseCommentSchema,
} from '@/types/showcase';

/**
 * Service for managing user interactions with showcase items
 */
export const interactionService = {
  /**
   * Record a user interaction with a showcase item
   */
  async recordInteraction(interaction: UserInteraction): Promise<UserInteraction> {
    const validatedInteraction = showcaseInteractionSchema.parse(interaction);
    const dbInteraction = mapUserInteractionToDb(validatedInteraction);

    // For likes, we need to ensure the user can only like once
    if (interaction.interactionType === 'like') {
      // Check if the user already liked this item
      const { data: existingLike, error: checkError } = await supabase
        .from('showcase_interactions')
        .select('*')
        .eq('user_id', interaction.userId)
        .eq('item_id', interaction.itemId)
        .eq('interaction_type', 'like')
        .maybeSingle();

      if (checkError) {
        console.error('Error checking existing like:', checkError);
        throw checkError;
      }

      // If already liked, return the existing interaction
      if (existingLike) {
        return mapDbToUserInteraction(existingLike);
      }

      // Increment the likes count on the showcase item
      const { error: incrementError } = await supabase
        .rpc('increment_showcase_likes', { item_id: interaction.itemId });

      if (incrementError) {
        console.error('Error incrementing likes count:', incrementError);
        throw incrementError;
      }
    }

    // For views, we might want to limit recording to once per session/day
    // This could be handled on the client side or with more complex logic here

    // Record the interaction
    const { data, error } = await supabase
      .from('showcase_interactions')
      .insert(dbInteraction)
      .select()
      .single();

    if (error) {
      console.error('Error recording interaction:', error);
      throw error;
    }

    return mapDbToUserInteraction(data);
  },

  /**
   * Check if a user has interacted with a showcase item in a specific way
   */
  async hasUserInteracted(
    userId: string,
    itemId: string,
    interactionType: UserInteraction['interactionType']
  ): Promise<boolean> {
    const { data, error } = await supabase
      .from('showcase_interactions')
      .select('id')
      .eq('user_id', userId)
      .eq('item_id', itemId)
      .eq('interaction_type', interactionType)
      .maybeSingle();

    if (error) {
      console.error('Error checking user interaction:', error);
      throw error;
    }

    return data !== null;
  },

  /**
   * Remove a user interaction
   */
  async removeInteraction(
    userId: string,
    itemId: string,
    interactionType: UserInteraction['interactionType']
  ): Promise<void> {
    const { error } = await supabase
      .from('showcase_interactions')
      .delete()
      .eq('user_id', userId)
      .eq('item_id', itemId)
      .eq('interaction_type', interactionType);

    if (error) {
      console.error('Error removing interaction:', error);
      throw error;
    }

    // If removing a like, decrement the likes count
    if (interactionType === 'like') {
      const { error: decrementError } = await supabase
        .rpc('decrement_showcase_likes', { item_id: itemId });

      if (decrementError) {
        console.error('Error decrementing likes count:', decrementError);
        throw decrementError;
      }
    }
  },

  /**
   * Get all interactions for a showcase item
   */
  async getItemInteractions(
    itemId: string,
    interactionType?: UserInteraction['interactionType']
  ): Promise<UserInteraction[]> {
    let query = supabase
      .from('showcase_interactions')
      .select('*')
      .eq('item_id', itemId);

    if (interactionType) {
      query = query.eq('interaction_type', interactionType);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching item interactions:', error);
      throw error;
    }

    return data.map(mapDbToUserInteraction);
  },

  /**
   * Get all interactions by a user
   */
  async getUserInteractions(
    userId: string,
    interactionType?: UserInteraction['interactionType']
  ): Promise<UserInteraction[]> {
    let query = supabase
      .from('showcase_interactions')
      .select('*')
      .eq('user_id', userId);

    if (interactionType) {
      query = query.eq('interaction_type', interactionType);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching user interactions:', error);
      throw error;
    }

    return data.map(mapDbToUserInteraction);
  },

  // Comment management functions

  /**
   * Add a comment to a showcase item
   */
  async addComment(comment: ShowcaseComment): Promise<ShowcaseComment> {
    const validatedComment = showcaseCommentSchema.parse(comment);
    const dbComment = mapShowcaseCommentToDb(validatedComment);

    const { data, error } = await supabase
      .from('showcase_comments')
      .insert(dbComment)
      .select()
      .single();

    if (error) {
      console.error('Error adding comment:', error);
      throw error;
    }

    // Also record the comment as an interaction
    await this.recordInteraction({
      userId: comment.userId,
      itemId: comment.itemId,
      interactionType: 'comment',
      metadata: { commentId: data.id }
    });

    return mapDbToShowcaseComment(data);
  },

  /**
   * Update an existing comment
   */
  async updateComment(id: string, content: string): Promise<ShowcaseComment> {
    const { data, error } = await supabase
      .from('showcase_comments')
      .update({ content, is_edited: true })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating comment:', error);
      throw error;
    }

    return mapDbToShowcaseComment(data);
  },

  /**
   * Delete a comment (soft delete)
   */
  async deleteComment(id: string): Promise<void> {
    const { error } = await supabase
      .from('showcase_comments')
      .update({ is_deleted: true, content: '[This comment has been deleted]' })
      .eq('id', id);

    if (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  },

  /**
   * Get all comments for a showcase item
   */
  async getItemComments(itemId: string): Promise<ShowcaseComment[]> {
    const { data, error } = await supabase
      .from('showcase_comments')
      .select('*, profiles(name, avatar_url)')
      .eq('item_id', itemId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching item comments:', error);
      throw error;
    }

    return data.map(mapDbToShowcaseComment);
  },

  /**
   * Get replies to a specific comment
   */
  async getCommentReplies(commentId: string): Promise<ShowcaseComment[]> {
    const { data, error } = await supabase
      .from('showcase_comments')
      .select('*, profiles(name, avatar_url)')
      .eq('parent_id', commentId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching comment replies:', error);
      throw error;
    }

    return data.map(mapDbToShowcaseComment);
  },

  /**
   * Fork a showcase item (creates a copy for the user)
   */
  async forkShowcaseItem(itemId: string, userId: string): Promise<string> {
    // This would call a server-side function to handle the complexity
    // of forking the underlying content based on item type
    const { data, error } = await supabase.rpc('fork_showcase_item', { 
      p_item_id: itemId,
      p_user_id: userId
    });

    if (error) {
      console.error('Error forking showcase item:', error);
      throw error;
    }

    // Record the fork interaction
    await this.recordInteraction({
      userId,
      itemId,
      interactionType: 'fork',
      metadata: { forkedItemId: data.new_item_id }
    });

    return data.new_item_id;
  }
};